import { useState, useEffect, useRef } from "react"
import { X, Crown, Mail, Phone, Briefcase, AtSign, Trash2, Loader2, ShieldCheck, Camera, Check, Plus, KeyRound, Lock } from "lucide-react"
import { Button } from "components/ui/button"
import { Input } from "components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "components/ui/dialog"
import { deleteUser, updateUser, updateCurrentUser, updateUserPassword, getUserTags, createUserTag, changePassword } from "services/users"
import { useUser } from "contexts/UserContext"
import api from "api/axios"
import { WorkerPermissionsPanel } from "components/authorization/WorkerPermissionsPanel"
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "components/ui/sheet"
import "./team-responsive.css"

const TAG_COLORS = ['#7b3fff', '#38bdf8', '#34d399', '#fbbf24', '#fb7185', '#e879f9']

interface UserTag {
  id: number
  name: string
  color: string
}

interface TeamMember {
  id: number
  name: string
  firstName?: string
  lastName?: string
  username: string
  email: string
  phone: string
  position?: string
  role: string
  isManager: boolean
  isSuperuser: boolean
  isOnline: boolean
  status?: "online" | "pausa" | "offline"
  avatarGradient: string
  memberSince: string
  tags?: UserTag[]
  profilePhotoUrl?: string | null
}

interface UserDetailsModalProps {
  member: TeamMember | null
  isOpen: boolean
  onClose: () => void
  onDeleted?: () => void
  onUpdated?: () => void
  currentUserId?: number
}

export function UserDetailsModal({ member, isOpen, onClose, onDeleted, onUpdated, currentUserId }: UserDetailsModalProps) {
  const { user: currentUser, refreshUser } = useUser()

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Phone inline edit
  const [editingPhone, setEditingPhone] = useState(false)
  const [phoneValue, setPhoneValue] = useState("")
  const [savingPhone, setSavingPhone] = useState(false)

  // Position inline edit
  const [editingPosition, setEditingPosition] = useState(false)
  const [positionValue, setPositionValue] = useState("")
  const [savingPosition, setSavingPosition] = useState(false)

  // Role inline change
  const [savingRole, setSavingRole] = useState(false)

  // Reset password
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [resetError, setResetError] = useState<string | null>(null)
  const [resetSaving, setResetSaving] = useState(false)

  // Photo upload
  const [photoUploading, setPhotoUploading] = useState(false)
  const photoInputRef = useRef<HTMLInputElement>(null)

  // Change own password
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [currentPwd, setCurrentPwd] = useState("")
  const [newPwd, setNewPwd] = useState("")
  const [confirmPwd, setConfirmPwd] = useState("")
  const [changePwdErrors, setChangePwdErrors] = useState<{ current?: string; new?: string; confirm?: string }>({})
  const [changePwdSaving, setChangePwdSaving] = useState(false)
  const [changePwdSuccess, setChangePwdSuccess] = useState(false)

  // Tags inline manager
  const [availableTags, setAvailableTags] = useState<UserTag[]>([])
  const [tagInputValue, setTagInputValue] = useState("")
  const [showTagDropdown, setShowTagDropdown] = useState(false)
  const [savingTags, setSavingTags] = useState(false)
  const tagInputRef = useRef<HTMLInputElement>(null)
  const tagDropdownRef = useRef<HTMLDivElement>(null)

  // Reset state when modal opens or member changes
  useEffect(() => {
    if (isOpen && member) {
      setEditingPhone(false)
      setEditingPosition(false)
      setPhoneValue(member.phone || "")
      setPositionValue(member.position || "")
      setShowResetPassword(false)
      setNewPassword("")
      setConfirmPassword("")
      setResetError(null)
      setDeleteError(null)
      setTagInputValue("")
      setShowTagDropdown(false)
      setShowChangePassword(false)
      setCurrentPwd("")
      setNewPwd("")
      setConfirmPwd("")
      setChangePwdErrors({})
      setChangePwdSuccess(false)
    }
  }, [isOpen, member])

  // Load available tags (for manager managing other user's tags)
  useEffect(() => {
    const isCurrentUserManager = currentUser?.role === 'manager' || currentUser?.is_superuser
    if (isOpen && isCurrentUserManager && member && member.id !== currentUserId) {
      getUserTags()
        .then(res => {
          if (res.data && Array.isArray(res.data)) setAvailableTags(res.data)
        })
        .catch(err => console.error("Error loading user tags:", err))
    }
  }, [isOpen, member, currentUser, currentUserId])

  // Click outside for tag dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(e.target as Node) &&
        tagInputRef.current &&
        !tagInputRef.current.contains(e.target as Node)
      ) {
        setShowTagDropdown(false)
      }
    }
    if (showTagDropdown) document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showTagDropdown])

  if (!member) return null

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

  const isOwnProfile = member.id === currentUserId
  const isCurrentUserManager = currentUser?.role === 'manager' || currentUser?.is_superuser
  const canChangePhoto = isOwnProfile
  const canEditPhonePosition = isOwnProfile
  const canChangeRole = isCurrentUserManager && !isOwnProfile && !member.isSuperuser
  const canManageTags = isCurrentUserManager && !isOwnProfile
  const canResetPassword = isCurrentUserManager && !isOwnProfile && !member.isSuperuser
  const canDelete = isCurrentUserManager && !isOwnProfile && !member.isSuperuser

  const memberTags = member.tags || []

  const handleDelete = async () => {
    setIsDeleting(true)
    setDeleteError(null)
    try {
      await deleteUser(member.id)
      setShowDeleteDialog(false)
      onDeleted?.()
      onClose()
    } catch (error: any) {
      console.error("Error deleting user:", error)
      setDeleteError(error.response?.data?.detail || "Errore durante l'eliminazione. Riprova.")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSavePhone = async () => {
    setSavingPhone(true)
    try {
      await updateCurrentUser({ phone: phoneValue })
      await refreshUser()
      onUpdated?.()
      setEditingPhone(false)
    } catch (e) {
      console.error("Error saving phone:", e)
    } finally {
      setSavingPhone(false)
    }
  }

  const handleSavePosition = async () => {
    setSavingPosition(true)
    try {
      await updateCurrentUser({ position: positionValue })
      await refreshUser()
      onUpdated?.()
      setEditingPosition(false)
    } catch (e) {
      console.error("Error saving position:", e)
    } finally {
      setSavingPosition(false)
    }
  }

  const handleRoleChange = async (newRole: string) => {
    if (newRole === member.role) return
    setSavingRole(true)
    try {
      await updateUser(member.id, { role: newRole })
      onUpdated?.()
    } catch (e) {
      console.error("Error changing role:", e)
    } finally {
      setSavingRole(false)
    }
  }

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoUploading(true)
    try {
      const formData = new FormData()
      formData.append('profile_photo', file)
      await api.patch('api/accounts/users/update_me/', formData)
      await refreshUser()
      onUpdated?.()
    } catch (err) {
      console.error("Error uploading photo:", err)
    } finally {
      setPhotoUploading(false)
      if (photoInputRef.current) photoInputRef.current.value = ""
    }
  }

  const handleResetPassword = async () => {
    setResetError(null)
    if (!newPassword || newPassword.length < 8) {
      setResetError("La password deve avere almeno 8 caratteri")
      return
    }
    if (newPassword !== confirmPassword) {
      setResetError("Le password non corrispondono")
      return
    }
    setResetSaving(true)
    try {
      await updateUserPassword(member.id, { password: newPassword })
      setShowResetPassword(false)
      setNewPassword("")
      setConfirmPassword("")
    } catch (e: any) {
      const apiErr = e.response?.data
      const msg = apiErr?.password
        ? (Array.isArray(apiErr.password) ? apiErr.password[0] : apiErr.password)
        : apiErr?.detail || "Errore durante il reset"
      setResetError(msg)
    } finally {
      setResetSaving(false)
    }
  }

  const handleChangeOwnPassword = async () => {
    const errs: { current?: string; new?: string; confirm?: string } = {}
    if (!currentPwd) errs.current = "Inserisci la password attuale"
    if (!newPwd || newPwd.length < 8) errs.new = "Minimo 8 caratteri"
    if (newPwd !== confirmPwd) errs.confirm = "Le password non corrispondono"
    if (Object.keys(errs).length > 0) {
      setChangePwdErrors(errs)
      return
    }
    setChangePwdErrors({})
    setChangePwdSaving(true)
    try {
      await changePassword({ current_password: currentPwd, new_password: newPwd })
      setChangePwdSuccess(true)
      setShowChangePassword(false)
      setCurrentPwd("")
      setNewPwd("")
      setConfirmPwd("")
      setTimeout(() => setChangePwdSuccess(false), 4000)
    } catch (e: any) {
      const apiErr = e.response?.data || {}
      const newErrs: { current?: string; new?: string; confirm?: string } = {}
      if (apiErr.current_password) {
        newErrs.current = Array.isArray(apiErr.current_password) ? apiErr.current_password[0] : apiErr.current_password
      }
      if (apiErr.new_password) {
        newErrs.new = Array.isArray(apiErr.new_password) ? apiErr.new_password[0] : apiErr.new_password
      }
      if (Object.keys(newErrs).length === 0) {
        newErrs.new = apiErr.detail || "Errore durante il cambio password"
      }
      setChangePwdErrors(newErrs)
    } finally {
      setChangePwdSaving(false)
    }
  }

  // Tag operations
  const filteredTags = availableTags.filter(
    tag =>
      tag.name.toLowerCase().includes(tagInputValue.toLowerCase()) &&
      !memberTags.some(mt => mt.id === tag.id)
  )
  const showCreateOption =
    tagInputValue.trim() &&
    !availableTags.some(t => t.name.toLowerCase() === tagInputValue.toLowerCase())

  const handleAddTag = async (tag: UserTag) => {
    setSavingTags(true)
    try {
      const newIds = [...memberTags.map(t => t.id), tag.id]
      await updateUser(member.id, { tag_ids: newIds })
      onUpdated?.()
      setTagInputValue("")
      setShowTagDropdown(false)
    } catch (e) {
      console.error("Error adding tag:", e)
    } finally {
      setSavingTags(false)
    }
  }

  const handleRemoveTag = async (tagId: number) => {
    setSavingTags(true)
    try {
      const newIds = memberTags.filter(t => t.id !== tagId).map(t => t.id)
      await updateUser(member.id, { tag_ids: newIds })
      onUpdated?.()
    } catch (e) {
      console.error("Error removing tag:", e)
    } finally {
      setSavingTags(false)
    }
  }

  const handleCreateTag = async () => {
    if (!tagInputValue.trim()) return
    setSavingTags(true)
    try {
      const colorIndex = availableTags.length % TAG_COLORS.length
      const res = await createUserTag({
        name: tagInputValue.trim(),
        color: TAG_COLORS[colorIndex],
      })
      const newTag = res.data
      setAvailableTags(prev => [...prev, newTag])
      const newIds = [...memberTags.map(t => t.id), newTag.id]
      await updateUser(member.id, { tag_ids: newIds })
      onUpdated?.()
      setTagInputValue("")
      setShowTagDropdown(false)
    } catch (e) {
      console.error("Error creating tag:", e)
    } finally {
      setSavingTags(false)
    }
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <SheetContent className="team-sheet w-full sm:max-w-[450px] h-[100dvh] gap-0 p-0 bg-[var(--hexa-modal-bg)] border-[rgba(106,77,255,0.25)]">
        <SheetDescription className="sr-only">Profilo, informazioni e azioni disponibili per il membro del team.</SheetDescription>
        {/* Header bar */}
        <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-[rgba(106,77,255,0.25)]">
          <SheetTitle className="text-[var(--hexa-text-primary)] font-semibold text-sm">Dettagli Utente</SheetTitle>
          <button
            onClick={onClose}
            aria-label="Chiudi profilo utente"
            className="text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto p-4 space-y-4 scrollbar-hexa">
          {/* A) HEADER PROFILO */}
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-2.5">
              {member.profilePhotoUrl ? (
                <div className="w-16 h-16 rounded-full overflow-hidden">
                  <img
                    src={member.profilePhotoUrl}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${member.avatarGradient} flex items-center justify-center`}>
                  <span className="text-[var(--hexa-text-primary)] font-semibold text-base">{getInitials(member.name)}</span>
                </div>
              )}
              {/* Status dot */}
              <div
                className="absolute bottom-0 right-0 w-3 h-3 rounded-full ring-2 ring-[var(--hexa-modal-bg)]"
                style={{
                  backgroundColor:
                    member.status === 'online' ? '#22c55e' :
                    member.status === 'pausa' ? '#f59e0b' :
                    member.status === 'offline' ? '#6b7280' :
                    (member.isOnline ? '#22c55e' : '#6b7280')
                }}
              />
              {/* Camera overlay (own profile only) */}
              {canChangePhoto && (
                <>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={photoUploading}
                    className="profile-photo-button absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] flex items-center justify-center shadow-lg border-2 border-[var(--hexa-modal-bg)] transition disabled:opacity-50"
                    title="Cambia foto"
                  >
                    {photoUploading ? (
                      <Loader2 className="w-3 h-3 text-white animate-spin" />
                    ) : (
                      <Camera className="w-3 h-3 text-white" />
                    )}
                  </button>
                </>
              )}
            </div>

            <div className="flex min-w-0 max-w-full flex-wrap justify-center items-center gap-2">
              <h3 className="min-w-0 max-w-full break-words [overflow-wrap:anywhere] text-base font-semibold text-[var(--hexa-text-primary)]">{member.name}</h3>
              {isOwnProfile && (
                <span
                  style={{
                    backgroundColor: 'var(--hexa-purple-light)',
                    color: 'var(--hexa-purple)',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: 500,
                  }}
                >
                  Tu
                </span>
              )}
            </div>

            {/* Role */}
            <div className="flex items-center gap-1.5 mt-1">
              {member.isManager && <Crown className="w-3 h-3 text-amber-400" />}
              {canChangeRole ? (
                <Select value={member.role} onValueChange={handleRoleChange} disabled={savingRole}>
                  <SelectTrigger
                    className="h-6 py-0 min-w-[100px] gap-1 [&>svg]:w-3 [&>svg]:h-3 [&>svg]:opacity-70 hover:bg-[var(--hexa-surface-hover)] transition-colors"
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid var(--hexa-border)',
                      color: 'var(--hexa-text-secondary)',
                      fontSize: '13px',
                      paddingLeft: '8px',
                      paddingRight: '6px',
                    }}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="worker">Worker</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-[var(--hexa-text-secondary)] text-sm">{member.role}</span>
              )}
            </div>

            {member.isSuperuser && (
              <span className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[10px] px-2 py-0.5 rounded-md font-semibold flex items-center gap-1 mt-2">
                <ShieldCheck className="w-3 h-3" />
                Amministratore
              </span>
            )}
            {(() => {
              const s = member.status || (member.isOnline ? 'online' : 'offline')
              const cfg = s === 'online'
                ? { bg: 'rgba(34,197,94,0.18)', fg: '#22c55e', label: 'Online' }
                : s === 'pausa'
                ? { bg: 'rgba(245,158,11,0.18)', fg: '#f59e0b', label: 'In pausa' }
                : { bg: 'rgba(107,114,128,0.18)', fg: '#9ca3af', label: 'Offline' }
              return (
                <span
                  className="text-xs px-2 py-1 rounded-full mt-2 inline-flex items-center gap-1.5"
                  style={{ backgroundColor: cfg.bg, color: cfg.fg }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cfg.fg }} />
                  {cfg.label}
                </span>
              )
            })()}
            {member.memberSince && (
              <span className="text-[var(--hexa-text-secondary)] text-xs mt-2">Membro dal {member.memberSince}</span>
            )}
          </div>

          {/* B) INFORMAZIONI */}
          <div>
            <h4 className="text-[var(--hexa-text-secondary)] text-xs uppercase tracking-wide mb-2.5">Informazioni</h4>
            <div className="bg-[var(--hexa-border-subtle)] rounded-lg p-3 space-y-2.5">
              {/* Username */}
              <div className="team-info-row flex items-center gap-2.5">
                <AtSign className="w-3.5 h-3.5 text-[var(--hexa-text-secondary)] flex-shrink-0" />
                <span className="text-[var(--hexa-text-primary)] text-xs">{member.username || "—"}</span>
              </div>

              {/* Email */}
              <div className="team-info-row flex items-center gap-2.5">
                <Mail className="w-3.5 h-3.5 text-[var(--hexa-text-secondary)] flex-shrink-0" />
                <span className="text-[var(--hexa-text-primary)] text-xs break-all">{member.email || "—"}</span>
              </div>

              {/* Phone */}
              <div className="team-info-row flex items-center gap-2.5">
                <Phone className="w-3.5 h-3.5 text-[var(--hexa-text-secondary)] flex-shrink-0" />
                {editingPhone ? (
                  <div className="flex items-center gap-1.5 flex-1">
                    <Input
                      value={phoneValue}
                      onChange={e => setPhoneValue(e.target.value)}
                      placeholder="+39 ..."
                      autoFocus
                      disabled={savingPhone}
                      className="h-6 text-xs bg-[var(--hexa-modal-bg)] border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] flex-1 px-2"
                    />
                    <button
                      onClick={handleSavePhone}
                      disabled={savingPhone}
                      className="text-emerald-500 hover:text-emerald-400 disabled:opacity-50"
                      title="Salva"
                    >
                      {savingPhone ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => { setEditingPhone(false); setPhoneValue(member.phone || "") }}
                      disabled={savingPhone}
                      className="text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] disabled:opacity-50"
                      title="Annulla"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : member.phone ? (
                  <span
                    className={`text-[var(--hexa-text-primary)] text-xs ${canEditPhonePosition ? 'cursor-pointer hover:text-[var(--hexa-purple)]' : ''}`}
                    onClick={() => canEditPhonePosition && setEditingPhone(true)}
                  >
                    {member.phone}
                  </span>
                ) : canEditPhonePosition ? (
                  <button
                    onClick={() => setEditingPhone(true)}
                    className="text-[var(--hexa-purple)] hover:text-[var(--hexa-purple-hover)] text-xs"
                  >
                    Aggiungi telefono
                  </button>
                ) : (
                  <span
                    className="italic"
                    style={{ color: 'var(--hexa-text-muted)', fontSize: '13px' }}
                  >
                    Non disponibile
                  </span>
                )}
              </div>

              {/* Position */}
              <div className="team-info-row flex items-center gap-2.5">
                <Briefcase className="w-3.5 h-3.5 text-[var(--hexa-text-secondary)] flex-shrink-0" />
                {editingPosition ? (
                  <div className="flex items-center gap-1.5 flex-1">
                    <Input
                      value={positionValue}
                      onChange={e => setPositionValue(e.target.value)}
                      placeholder="es. Sales Lead"
                      autoFocus
                      disabled={savingPosition}
                      className="h-6 text-xs bg-[var(--hexa-modal-bg)] border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] flex-1 px-2"
                    />
                    <button
                      onClick={handleSavePosition}
                      disabled={savingPosition}
                      className="text-emerald-500 hover:text-emerald-400 disabled:opacity-50"
                      title="Salva"
                    >
                      {savingPosition ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => { setEditingPosition(false); setPositionValue(member.position || "") }}
                      disabled={savingPosition}
                      className="text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-primary)] disabled:opacity-50"
                      title="Annulla"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : member.position ? (
                  <span
                    className={`text-[var(--hexa-text-primary)] text-xs ${canEditPhonePosition ? 'cursor-pointer hover:text-[var(--hexa-purple)]' : ''}`}
                    onClick={() => canEditPhonePosition && setEditingPosition(true)}
                  >
                    {member.position}
                  </span>
                ) : canEditPhonePosition ? (
                  <button
                    onClick={() => setEditingPosition(true)}
                    className="text-[var(--hexa-purple)] hover:text-[var(--hexa-purple-hover)] text-xs"
                  >
                    Aggiungi posizione
                  </button>
                ) : (
                  <span
                    className="italic"
                    style={{ color: 'var(--hexa-text-muted)', fontSize: '13px' }}
                  >
                    Non disponibile
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* C) TAG */}
          <div>
            <h4 className="text-[var(--hexa-text-secondary)] text-xs uppercase tracking-wide mb-2.5">Tag</h4>
            <div className="bg-[var(--hexa-border-subtle)] rounded-lg p-3">
              {canManageTags ? (
                <div className="relative">
                  <div className="w-full min-h-[34px] flex flex-wrap items-center gap-1.5">
                    {memberTags.map(tag => (
                      <span
                        key={tag.id}
                        className="team-tag inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border"
                        style={{
                          backgroundColor: `${tag.color}33`,
                          color: tag.color,
                          borderColor: `${tag.color}66`,
                        }}
                      >
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                        {tag.name}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag.id)}
                          disabled={savingTags}
                          className="ml-0.5 hover:opacity-70 transition disabled:opacity-50"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                    <input
                      ref={tagInputRef}
                      type="text"
                      placeholder={memberTags.length === 0 ? "Cerca o crea tag..." : "+"}
                      value={tagInputValue}
                      onChange={e => { setTagInputValue(e.target.value); setShowTagDropdown(true) }}
                      onFocus={() => setShowTagDropdown(true)}
                      disabled={savingTags}
                      className="flex-1 min-w-[60px] bg-transparent border-none text-[var(--hexa-text-primary)] text-xs focus:outline-none placeholder:text-[var(--hexa-text-muted)]"
                    />
                  </div>
                  {showTagDropdown && (tagInputValue || filteredTags.length > 0) && (
                    <div
                      ref={tagDropdownRef}
                      className="absolute z-50 w-full mt-1 bg-[var(--hexa-modal-bg)] border border-[rgba(106,77,255,0.25)] rounded-lg shadow-xl max-h-32 overflow-y-auto scrollbar-hexa"
                    >
                      {filteredTags.map(tag => (
                        <div
                          key={tag.id}
                          onClick={() => handleAddTag(tag)}
                          className="px-2.5 py-1.5 hover:bg-[var(--hexa-button-cancel)] cursor-pointer flex items-center gap-2"
                        >
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                          <span className="text-xs text-[var(--hexa-text-primary)]">{tag.name}</span>
                        </div>
                      ))}
                      {showCreateOption && (
                        <div
                          onClick={handleCreateTag}
                          className="px-2.5 py-1.5 hover:bg-[var(--hexa-button-cancel)] cursor-pointer flex items-center gap-2 border-t border-[rgba(106,77,255,0.25)]"
                        >
                          <Plus className="w-3 h-3 text-[var(--hexa-purple)]" />
                          <span className="text-xs text-[var(--hexa-text-primary)]">
                            Crea "<span className="font-medium text-[var(--hexa-purple)]">{tagInputValue.trim()}</span>"
                          </span>
                        </div>
                      )}
                      {filteredTags.length === 0 && !showCreateOption && tagInputValue && (
                        <div className="px-2.5 py-1.5 text-xs text-[var(--hexa-text-secondary)]">Nessun tag trovato</div>
                      )}
                    </div>
                  )}
                </div>
              ) : memberTags.length === 0 ? (
                <p className="text-[var(--hexa-text-secondary)] text-xs text-center">Nessun tag assegnato</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {memberTags.map(tag => (
                    <span
                      key={tag.id}
                      className="team-tag inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border"
                      style={{
                        backgroundColor: `${tag.color}33`,
                        color: tag.color,
                        borderColor: `${tag.color}66`,
                      }}
                    >
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* C2) SICUREZZA — solo proprio profilo */}
          {isOwnProfile && (
            <div>
              <h4 className="text-[var(--hexa-text-secondary)] text-xs uppercase tracking-wide mb-2.5">Sicurezza</h4>
              <div className="bg-[var(--hexa-border-subtle)] rounded-lg p-3">
                {changePwdSuccess && !showChangePassword && (
                  <p className="text-emerald-500 text-xs mb-2 flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" />
                    Password aggiornata
                  </p>
                )}
                {showChangePassword ? (
                  <div className="space-y-2">
                    <div>
                      <Input
                        type="password"
                        placeholder="Password attuale"
                        value={currentPwd}
                        onChange={e => { setCurrentPwd(e.target.value); if (changePwdErrors.current) setChangePwdErrors(prev => ({ ...prev, current: undefined })) }}
                        disabled={changePwdSaving}
                        className={`bg-[var(--hexa-modal-bg)] border text-[var(--hexa-text-primary)] text-xs h-8 ${changePwdErrors.current ? 'border-rose-500' : 'border-[rgba(106,77,255,0.25)]'}`}
                      />
                      {changePwdErrors.current && (
                        <p className="text-rose-500 text-[11px] mt-1">{changePwdErrors.current}</p>
                      )}
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="Nuova password"
                        value={newPwd}
                        onChange={e => { setNewPwd(e.target.value); if (changePwdErrors.new) setChangePwdErrors(prev => ({ ...prev, new: undefined })) }}
                        disabled={changePwdSaving}
                        className={`bg-[var(--hexa-modal-bg)] border text-[var(--hexa-text-primary)] text-xs h-8 ${changePwdErrors.new ? 'border-rose-500' : 'border-[rgba(106,77,255,0.25)]'}`}
                      />
                      {changePwdErrors.new && (
                        <p className="text-rose-500 text-[11px] mt-1">{changePwdErrors.new}</p>
                      )}
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="Conferma nuova password"
                        value={confirmPwd}
                        onChange={e => { setConfirmPwd(e.target.value); if (changePwdErrors.confirm) setChangePwdErrors(prev => ({ ...prev, confirm: undefined })) }}
                        disabled={changePwdSaving}
                        className={`bg-[var(--hexa-modal-bg)] border text-[var(--hexa-text-primary)] text-xs h-8 ${changePwdErrors.confirm ? 'border-rose-500' : 'border-[rgba(106,77,255,0.25)]'}`}
                      />
                      {changePwdErrors.confirm && (
                        <p className="text-rose-500 text-[11px] mt-1">{changePwdErrors.confirm}</p>
                      )}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setShowChangePassword(false)
                          setCurrentPwd("")
                          setNewPwd("")
                          setConfirmPwd("")
                          setChangePwdErrors({})
                        }}
                        disabled={changePwdSaving}
                        className="flex-1 text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] text-xs h-8"
                      >
                        Annulla
                      </Button>
                      <Button
                        onClick={handleChangeOwnPassword}
                        disabled={changePwdSaving}
                        className="flex-1 bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white text-xs h-8"
                      >
                        {changePwdSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Salva"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => { setShowChangePassword(true); setChangePwdSuccess(false) }}
                    className="inline-flex items-center gap-1.5 text-[var(--hexa-purple)] hover:text-[var(--hexa-purple-hover)] text-xs"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    Cambia password
                  </button>
                )}
              </div>
            </div>
          )}

          {/* D) AZIONI MANAGER */}
          {isCurrentUserManager && !isOwnProfile && member.role === 'worker' && (
            <WorkerPermissionsPanel userId={member.id} />
          )}

          {(canResetPassword || canDelete) && (
            <div>
              <h4 className="text-[var(--hexa-text-secondary)] text-xs uppercase tracking-wide mb-2.5">Azioni Manager</h4>
              <div className="space-y-2">
                {/* Reset password */}
                {canResetPassword && (
                  showResetPassword ? (
                    <div className="bg-[var(--hexa-border-subtle)] rounded-lg p-3 space-y-2">
                      <Input
                        type="password"
                        placeholder="Nuova password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        disabled={resetSaving}
                        className="bg-[var(--hexa-modal-bg)] border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] text-xs h-8"
                      />
                      <Input
                        type="password"
                        placeholder="Conferma password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        disabled={resetSaving}
                        className="bg-[var(--hexa-modal-bg)] border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)] text-xs h-8"
                      />
                      {resetError && <p className="text-rose-500 text-xs">{resetError}</p>}
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setShowResetPassword(false)
                            setNewPassword("")
                            setConfirmPassword("")
                            setResetError(null)
                          }}
                          disabled={resetSaving}
                          className="flex-1 text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] text-xs h-8"
                        >
                          Annulla
                        </Button>
                        <Button
                          onClick={handleResetPassword}
                          disabled={resetSaving}
                          className="flex-1 bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white text-xs h-8"
                        >
                          {resetSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Salva"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() => setShowResetPassword(true)}
                      className="w-full justify-start bg-[var(--hexa-border-subtle)] hover:bg-[var(--hexa-border)] text-[var(--hexa-text-primary)] text-xs h-9"
                    >
                      <KeyRound className="w-3.5 h-3.5 mr-2" />
                      Reset password
                    </Button>
                  )
                )}

                {/* Delete */}
                {canDelete && (
                  <Button
                    variant="ghost"
                    onClick={() => setShowDeleteDialog(true)}
                    className="w-full justify-start bg-[var(--hexa-border-subtle)] hover:bg-rose-500/10 text-rose-500 hover:text-rose-400 text-xs h-9"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-2" />
                    Elimina utente
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="team-dialog bg-[var(--hexa-modal-bg)] border-[rgba(106,77,255,0.25)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--hexa-text-primary)]">Conferma eliminazione</DialogTitle>
            <DialogDescription className="text-[var(--hexa-text-secondary)]">
              Sei sicuro di voler eliminare <strong>{member.name}</strong>? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>

          {deleteError && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3">
              <p className="text-rose-500 text-sm">{deleteError}</p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowDeleteDialog(false); setDeleteError(null) }}
              disabled={isDeleting}
              className="bg-[var(--hexa-button-cancel)] border-[rgba(106,77,255,0.25)] text-[var(--hexa-text-primary)]"
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Eliminazione...
                </>
              ) : (
                "Elimina"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
